<template>
  <div :class="menuClasses">
    <div class="flex flex-col gap-2">
      <div :class="['flex items-center p-2', navBar.shown ? 'justify-end' : 'justify-center']">
        <PanelLeftOpen
          color="#03619C"
          class="cursor-pointer rounded-sm hover:bg-sky-100"
          @click="navBar.toggle"
        />
      </div>
      <div class="flex w-full items-center justify-center py-4">
        <img src="@/assets/LogoManagement.svg" class="w-32" alt="logo" />
      </div>
      <div v-if="navBar.shown" class="flex items-center justify-center text-sky-800">
        <span class="font-bold">
          Olá,
          <span class="italic">{{ userName }}!</span>
        </span>
      </div>
    </div>
    <div class="flex w-full flex-col gap-4 overflow-hidden overflow-y-auto">
      <PanelMenu
        :model="permissionsNavbarRouter"
        :class="[navBar.shown ? '' : 'flex items-center justify-center']"
      >
        <template #item="{ item, props }">
          <router-link v-if="item.to" v-slot="{ href, navigate }" :to="item.to" custom>
            <a
              :href="href"
              v-bind="props.action"
              @click="navigate"
              class="flex h-10 items-center gap-2"
            >
              <Icons :icon="item.icon" class="font-bold" />
              <span v-if="navBar.shown" class="font-bold">
                {{ languageStore.t(item.label as string) }}
              </span>
            </a>
          </router-link>

          <a
            v-else
            v-bind="props.action"
            class="flex h-10 items-center justify-between gap-2"
            v-if="navBar.shown"
          >
            <div class="flex items-center gap-2">
              <Icons :icon="item.icon" class="font-bold" />
              <span v-if="navBar.shown" class="font-bold">
                {{ languageStore.t(item.label as string) }}
              </span>
            </div>
            <Icons v-if="navBar.shown" icon="chevron-down" class="font-bold" />
          </a>
        </template>
      </PanelMenu>
    </div>

    <div v-if="navBar.shown" class="bottom-2 flex flex-col items-center gap-4">
      <div
        class="flex cursor-pointer items-center justify-center gap-2 rounded-md bg-sky-100 p-2"
        @click="logout"
      >
        <LogOut :size="16" :stroke-width="2.5" color="#03619C" />
        <span class="font-bold text-sky-800">{{ languageStore.t('general.exit') }}</span>
      </div>

      <div class="flex flex-col items-center justify-center gap-6">
        <SelectLanguage class="mx-2" />
        <div class="flex flex-row items-center justify-center gap-2">
          <span class="text-sm font-bold text-sky-800">Version: {{ appVersion }}</span>
          <!-- <span class="text-sm font-bold text-sky-800">API version: {{ apiVersionLabel }}</span> -->
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Icons from '@/components/subcomponents/Icons.vue'
import { hasPermission } from '@/composables/RequiredPermissions'
import { UserPermission } from '@/models/enums/permissions'
import { INavBarItem } from '@/models/navbar'
import { getAppVersion } from '@/services/appService'
import * as loginServices from '@/services/userService'
import { getUserName } from '@/services/userService'
import { getBackendVersion } from '@/services/versionService'
import { useLanguageStore } from '@/stores/LanguageStore'
import { useNavBarStore } from '@/stores/NavBarStore'
import { LogOut, PanelLeftOpen } from 'lucide-vue-next'
import PanelMenu from 'primevue/panelmenu'
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

import SelectLanguage from '../subcomponents/SelectLanguage.vue'

const router = useRouter()
const navBar = useNavBarStore()
const languageStore = useLanguageStore()

const permissionsNavbarRouter = computed(() => {
  const filterRoutes = (routes: INavBarItem[]): INavBarItem[] => {
    return routes
      .map((route): INavBarItem | null => {
        const routeCopy = { ...route }

        if (route.items) {
          const filteredItems: INavBarItem[] = filterRoutes(route.items)
          routeCopy.items = filteredItems

          return filteredItems.length > 0 ? routeCopy : null
        }

        if (!route.hasOwnProperty('requiredPermission')) {
          return routeCopy
        }

        return route.requiredPermission && hasPermission(route.requiredPermission as UserPermission)
          ? routeCopy
          : null
      })
      .filter((item): item is INavBarItem => item !== null)
  }

  return filterRoutes(navBar.routes)
})

const logout = () => loginServices.logout(router)
const userName = getUserName()
const appVersion = getAppVersion()
const apiVersionLabel = ref('…')

onMounted(() => {
  void (async () => {
    try {
      const { data } = await getBackendVersion()
      apiVersionLabel.value = data.version
    } catch {
      apiVersionLabel.value = '—'
    }
  })()
})
const menuClasses = computed(() => [
  navBar.shown ? 'w-[20rem] grid grid-rows-[auto_1fr_auto]' : 'w-[4rem] flex flex-col',
  'gap-4 fixed left-0 top-0 z-50 h-screen bg-white p-2 shadow-lg transition-all duration-300',
])
</script>

<style>
.p-panelmenu {
  border: none !important;
  box-shadow: none !important;
  gap: 0 !important;
}
.p-panelmenu-panel {
  border-width: 0px 0px 1px 0px !important;
  border-radius: 0 !important;
  margin: 0 !important;
}
</style>
